import { useEffect, useState } from "react";
import type { DesktopsView } from "../../shared/desktops";
import { useRuns } from "../lib/agent";
import { api } from "../lib/api";
import { cx } from "./ui";

/**
 * Desktop switcher of the live view (several desktops on the cloud computer): one button per desktop the computer's
 * size allows, a dot on the desktops a mission works on, and the RAM that sets how many there are. Hidden when the
 * computer has a single desktop.
 */
export function DesktopSwitcher({
  machineId,
  value,
  onChange,
  dark = false,
  className,
}: {
  machineId: string;
  value: number;
  onChange: (desktop: number) => void;
  /** On the dark stage of /ordinateur. */
  dark?: boolean;
  className?: string;
}) {
  const [view, setView] = useState<DesktopsView | null>(null);
  // Which task works on which desktop (parallel lanes: one desktop each).
  const runs = useRuns({ poll: false }).data ?? [];
  const titleOf = (runId: string | null) => (runId ? (runs.find((r) => r.id === runId)?.title ?? null) : null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api("GET /api/machines/:id/desktops", { params: { id: machineId } }).then(
        (v) => alive && setView(v),
        () => undefined,
      );
    void load();
    const t = setInterval(load, 15_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [machineId]);

  if (!view || view.cap <= 1) return null;
  return (
    <div data-testid="desktop-switcher" className={cx("flex min-w-0 flex-wrap items-center gap-1.5", className)}>
      <span className={cx("text-[12.5px]", dark ? "text-white/70" : "text-muted")}>Bureaux</span>
      {view.desktops.map((d) => (
        <button
          key={d.index}
          type="button"
          aria-pressed={d.index === value}
          aria-label={`Bureau ${d.index + 1}${d.busy ? ` (${titleOf(d.runId) ?? "une mission y travaille"})` : ""}`}
          title={titleOf(d.runId) ?? undefined}
          onClick={() => onChange(d.index)}
          className={cx(
            "inline-flex h-7 min-w-7 items-center justify-center gap-1 rounded-full border px-2 text-[12.5px] tabular-nums",
            dark
              ? d.index === value
                ? "border-white bg-white text-[#14121a]"
                : "border-white/25 bg-white/10 text-white/80 hover:text-white"
              : d.index === value
                ? "border-accent bg-accent text-accent-fg"
                : "border-line bg-elev text-muted hover:text-fg",
          )}
        >
          {d.index + 1}
          {d.busy ? <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden /> : null}
        </button>
      ))}
      <span className={cx("text-[11.5px]", dark ? "text-white/55" : "text-subtle")}>
        {view.cap} bureaux avec {view.memoryGiB} Go de mémoire
      </span>
      {view.desktops.filter((d) => d.busy && titleOf(d.runId)).length > 1 ? (
        <span data-testid="desktop-lanes" className={cx("flex basis-full flex-wrap items-center gap-1.5 text-[11.5px]", dark ? "text-white/70" : "text-muted")}>
          <span>En parallèle :</span>
          {view.desktops
            .filter((d) => d.busy && titleOf(d.runId))
            .map((d) => (
              <span
                key={d.index}
                data-testid="desktop-lane"
                className={cx(
                  "inline-flex max-w-[16rem] min-w-0 items-center gap-1 rounded-full border px-2 py-0.5",
                  dark ? "border-white/20 bg-white/10" : "border-line bg-elev",
                )}
              >
                <span className={cx("font-medium tabular-nums", dark ? "text-white" : "text-fg")}>Bureau {d.index + 1}</span>
                <span aria-hidden>·</span>
                <span className="truncate">{titleOf(d.runId)}</span>
              </span>
            ))}
        </span>
      ) : null}
    </div>
  );
}
